import { ShotsContextInterface } from '../contexts/shots/provider';
import { InvitedPresenter, Session } from '../components/app-entry/producer/meeting-modal';
import { baseSettings, initialSettingsState } from '../stores/reducers/settings';
import { APP } from '../stores/session-status';
import { buildSyncUrl, getHeaders, getSyncHeaders } from './utils';
import { showAlert } from '../components/general-components/alert-service';
import { ChannelAuthMethod, HttpRequest } from '../stores/types/types';
import { Entity } from '../types/interfaces';
import { Profile } from '../stores/reducers/invitations';

export const CreateOrUpdateMeeting = (
	token: string,
	id: string | undefined,
	// Made it "any" type because when saving new SGR meeting in meeting-modal.tsx
	// it doesn't have all the "Meeting" fields yet
	meeting: any,
	app: string,
	standaloneTitle?: string,
	invites?: InvitedPresenter[],
	sessions?: Session[],
	showroomsToken?: string,
	meetingId?: string,
) => {
	const settingsJson = meeting?.settings_json && meeting.settings_json !== '{}'
		? meeting.settings_json
		: JSON.stringify(initialSettingsState);
	const body = {
		channel: meeting.channel,
		start_time: meeting.start_time,
		end_time: meeting.end_time,
		settings_json: settingsJson,
		language: meeting?.language
			?? meeting?.default_language
			?? meeting?.languages?.[0]
			?? null,
		base_shot_style: baseSettings, // Send the base shot style for preloaded shots in new meetings
	} as any;

	const headers = getHeaders(token) as any;

	switch (app) {
		case APP.EVENTS:
			body.events_session_uuid = id;
			if (meetingId) {
				body.meeting_id = meetingId;
			}
			if (meeting?.meeting) {
				body.meeting = meeting.meeting;
			}
			break;
		case APP.EVENTS_LEGACY:
			body.brandlive_event = id;
			break;
		case APP.SHOWROOMS:
			body.showrooms_session_id = id;
			headers['showrooms-token'] = showroomsToken;
			break;
		case APP.ALL_HANDS:
			body.allhands_id = id;
			break;
		case APP.GREENROOM:
			body.meeting = id;
			body.meeting_id = meetingId;
			body.standalone_title = standaloneTitle;
			body.invites = invites;
			body.sessions = sessions;
			break;
		default:
			// do nothing
	}

	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/create-or-update-meeting`, {
		method: 'POST',
		headers,
		body: JSON.stringify(body),
	}).then((result) => {
		if (result.ok) {
			return result.json();
		}
		return null;
	});
};

export const LoadMeeting = (
	token: string,
	id: string | number,
	showroomsToken?: string,
	selectedApp?: string,
) => {
	const headers = getHeaders(token, id) as any;
	if (showroomsToken) {
		headers['showrooms-token'] = showroomsToken;
	}
	const url = `${process.env.REACT_APP_API_HOST}/v2/greenroom/${id}`;
	return fetch(url, {
		method: 'GET',
		headers,
	}).then(async (result) => {
		if (result.ok) {
			const meetingJson = await result.json();

			if ((meetingJson.settings_json === '{}' || !meetingJson.settings_json) && selectedApp) {
				meetingJson.settings_json = JSON.stringify(initialSettingsState);
				await CreateOrUpdateMeeting(
					token,
					id.toString(),
					meetingJson,
					selectedApp,
					meetingJson.standalone_title,
					undefined,
					undefined,
					showroomsToken,
					meetingJson.meeting_id,
				);
			}

			return meetingJson;
		}
		return null;
	});
};

export const LoadMeetingFromShowroomsID = (sessionID: string | number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/showrooms/meeting/${sessionID}`, {
	method: 'GET',
	headers: getHeaders() as any,
}).then(async (result) => {
	if (result.ok) {
		return result.json();
	}
	return null;
});

export const LoadMeetingsEventsProducer = (token: string, channel: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/broadcast-sessions/${channel}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/broadcast-sessions/${channel} status ${result.status}`);
	return null;
}).catch((e) => console.log(e));

export const LoadMeetingsEventsLegacyProducer = (token: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/meetings-events-legacy-producer`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/meetings-events-legacy-producer status ${result.status}`);
	return null;
}).catch((e) => console.log(e));

export const LoadMeetingsEventsLegacyProducerByChannel = (token: string, channel: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/meetings-events-legacy-producer/${channel}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/meetings-events-legacy-producer/${channel} status ${result.status}`);
	return null;
}).catch((e) => console.log(e));

export const LoadMeetingsStandaloneGreenroomProducer = (token: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/standalone/meetings`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Unable to load meetings');
});

export const LoadMeetingsStandaloneGreenroomProducerByChannel = (token: string, channel: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/standalone/meetings/${channel}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/standalone/meetings/${channel} status ${result.status}`);
	return null;
});

export const CopyMeeting = (token: string, fromMeetingId: number, toMeetingId: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/copy-meeting`, {
	method: 'POST',
	headers: getHeaders(token) as any,
	body: JSON.stringify({
		fromMeetingId,
		toMeetingId,
	}),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	showAlert({
		message: 'Copy Unsuccessful',
		description: 'We ran into an issue copying your greenroom. Please contact support.',
		type: 'error',
		duration: 5000,
	});
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/copy-meeting status ${result.status}`);
	return null;
});

export const DeleteMeeting = (token: string, meetingId: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/standalone/meetings/${meetingId}`, {
	method: 'DELETE',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/standalone/meetings/${meetingId} ${result.status}`);
	return undefined;
});

export const CreateInvitationLegacy = (
	token: string,
	meeting: number,
	channelName: string,
	title: string,
	email: string,
	channel: number,
	watcherOnly?: boolean,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/create-invitation`, {
	method: 'POST',
	headers: getHeaders(token) as any,
	body: JSON.stringify({
		channel,
		meeting,
		channel_name: channelName,
		title,
		email,
		watcher_only: watcherOnly,
	}),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.log(result.status);
	throw new Error('Error creating invitation');
});

export interface NewInvitation {
	/* eslint-disable camelcase */
	email: string;
	watcher_only: boolean;
	first_name?: string;
	last_name?: string;
	avatar?: string;
	job_title?: string;
	gr_user?: number;
	authentication?: ChannelAuthMethod;
	isSsoOnly?: boolean;
}

// This can replace CreateInvitationLegacy and CreateInvitationsLegacy
export const CreateInvitations = (
	token: string,
	meeting: number,
	channelName: string,
	title: string,
	channel: number,
	invitations: NewInvitation[],
) => fetch(`${process.env.REACT_APP_API_HOST}/v3/greenroom/create-invitations`, {
	method: 'POST',
	headers: getHeaders(token),
	body: JSON.stringify({
		meeting,
		channel_name: channelName,
		title,
		channel,
		invitations,
	}),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}

	console.log(result.status);
	throw new Error('Error creating invitations');
});

export const CreateInvitationsLegacy = (
	token: string,
	meeting: number,
	channelName: string,
	title: string,
	channel: number,
	invitations: NewInvitation[],
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/create-invitations`, {
	method: 'POST',
	headers: getHeaders(token) as any,
	body: JSON.stringify({
		meeting,
		channel_name: channelName,
		title,
		channel,
		invitations,
	}),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.log(result.status);
	throw new Error('Error creating invitations');
});

export const RevokeInvitation = (
	token: string,
	meeting: number,
	email: string | number,
	channel: number,
	stripFeedsCallback: ShotsContextInterface['stripFeedFromShots'] | undefined,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/revoke-invitation`, {
	method: 'POST',
	headers: getHeaders(token) as any,
	body: JSON.stringify({
		channel,
		meeting,
		email,
	}),
}).then((result) => {
	if (result.ok) {
		if (stripFeedsCallback) {
			stripFeedsCallback(
				typeof email === 'number' ? email : null,
				typeof email === 'string' ? email : null,
				typeof email === 'number' ? email : null,
				typeof email === 'string' ? email : null,
			);
		}
		return email;
	}
	console.log(result.status);
	throw new Error('Unable to revoke invitation');
});

export interface MeetingInvitation {
	code: string;
	watcher_only: boolean;
	profile: Profile;
	authentication: ChannelAuthMethod;
	isSsoOnly: boolean;
}

// MeetingInvitation[] is what gets returned, but would need to solve type errors to use.
export const LoadInvitations = (token: string, meeting: number) => (
	fetch(
		`${process.env.REACT_APP_API_HOST}/v3/greenroom/invitations/${meeting}`,
		{
			method: 'GET',
			headers: getHeaders(token),
		},
	).then((result) => {
		if (result.ok) {
			return result.json();
		}

		throw new Error('Unable to load invitations');
	})
);

export const InviteAccepted = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/invitation/accepted/${meeting}`, {
	method: 'PUT',
	headers: getHeaders(token) as any,
}).then((result) => result.ok);

export const IsProducerForEventsSession = (token: string, meetingId: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/is-producer-for-session/${meetingId}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.log(result.status);
	throw new Error('Unable to load IsProducerForEventsSession');
});

export const requestMeetingJSON = async (
	token: string,
	meeting: number | string,
	title: string,
) => {
	try {
		const result = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/export-meeting/${meeting}`, {
			method: 'GET',
			headers: getHeaders(token) as any,
		});

		if (result.ok) {
			const meetingData = await result.json();

			// MeetingJSON data can only be imported using Standalone
			// and there should always be a standalone title for Standalone meetings.
			if (meetingData?.meeting && !meetingData?.meeting?.standalone_title) {
				meetingData.meeting.standalone_title = title;
			}

			const downloadData = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(meetingData))}`;
			const downloadAnchorNode = document.createElement('a');
			const meetingCode = meetingData?.meeting?.meeting_id ?? meeting; // just in case
			downloadAnchorNode.setAttribute('href', downloadData);
			downloadAnchorNode.setAttribute('download', `${meetingCode}.json`);
			document.body.appendChild(downloadAnchorNode); // required for firefox
			downloadAnchorNode.click();
			downloadAnchorNode.remove();

			return meetingData;
		}
		const message = `[${result.status}] ${result.statusText}`;
		alert(message);
		return {
			error: message,
		};
	} catch (e) {
		console.error(e);
		return {
			// stupid typescript not knowing e is an Error and not being able to type it
			error: e instanceof Error
				? e.message
				: 'error',
		};
	}
};

export const requestImportMeetingJSON = async (
	token: string,
	meetingData: string,
	channel: number,
	cb: Function,
) => {
	try {
		const result = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/import-meeting/${channel}`, {
			method: 'POST',
			headers: getHeaders(token) as any,
			body: meetingData,
		});

		if (result.ok) {
			const mData = await result.json();
			cb(mData);
			return mData;
		}
		const message = `[${result.status}] ${result.statusText}`;
		alert(message);
		return {
			error: message,
		};
	} catch (e) {
		console.error(e);

		// stupid typescript not knowing e is an Error and not being able to type it
		const message = e instanceof Error
			? e.message
			: 'error';

		alert(message);
		return {
			error: message,
		};
	}
};

export const LoadOnsiteUsers = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/onsite-users/${meeting}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Unable to load onsite users');
});

export const UpdateUserMeetingSettings = (token: string, meetingId: number, userId: number, settings: any) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user-meeting-settings`, {
	method: 'POST',
	headers: getHeaders(token) as any,
	body: JSON.stringify({ meetingId, settings, userId }),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Unable to update user meeting settings.');
});

export const LoadEventsMeetingByUUID = (token: string, uuid: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/get-session-by-uuid/${uuid}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/get-session-by-uuid/${uuid} status ${result.status}`);
	return null;
}).catch((e) => console.log(e));

export const GetRoomStateEntity = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/room-state/${roomId}`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});
		const entity = await res.json();
		return entity;
	} catch (error) {
		console.error(error);
		return null;
	}
};

export const UpdateRoomStateEntity = async (
	token: string,
	roomId: number,
	userId: number,
	newRoomStateEntity: Entity,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room-state/${roomId}/${userId}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({ newRoomStateEntity }),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}
	return false;
};

export const CheckEventPublishedStatus = (
	token: string,
	uuid: string,
) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/published-status/${uuid}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	console.error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/published-status/${uuid} status ${result.status}`);
	return null;
}).catch((e) => console.log(e));

export const LoadAdvancedAudioUsers = (token: string, meeting: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/advanced-audio-users/${meeting}`, {
	method: 'GET',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Unable to load advanced audio users');
});
